'use client';

import { useState } from 'react';
import { Button as DsButton } from '@/components/ds';
import GroupHeader from './GroupHeader';

type TeaserGroup = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  privacy: string;
  banner?: string | null;
  avatar_url?: string | null;
  tags: string[];
  rules?: string | null;
};

// Read-only teaser shown to a non-member of a REQUEST-privacy group: header +
// description + tags + member count, the group's rules (if any), and a request
// panel. Chat is locked behind approval. This is the one place rules are
// surfaced + acknowledged. Public groups never see this (one-click join).
export default function RequestToJoinPanel({
  group,
  initialStatus,
}: {
  group: TeaserGroup;
  initialStatus: 'none' | 'pending' | 'approved' | 'denied';
}) {
  const [status, setStatus] = useState(initialStatus);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/groups/${group.id}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Could not send your request. Please try again.');
        return;
      }
      setStatus('pending');
    } catch {
      setError('Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/groups/${group.id}/requests`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setStatus('none');
      setMessage('');
    } catch {
      setError('Could not cancel your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface-0 text-text-primary min-h-[calc(100dvh-112px)] md:min-h-[calc(100dvh-48px)]">
      <GroupHeader
        id={group.id}
        name={group.name}
        description={group.description}
        memberCount={group.memberCount}
        privacy={group.privacy}
        banner={group.banner ?? 'default'}
        avatarUrl={group.avatar_url ?? null}
        canManage={false}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {group.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {group.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] bg-surface-3 text-text-secondary border border-border-subtle"
              >
                #{t}
              </span>
            ))}
          </div>
        )}

        {group.rules && (
          <div className="rounded-md border border-border-subtle bg-surface-1 p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-2">Group rules</h3>
            <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">
              {group.rules}
            </p>
          </div>
        )}

        <div className="rounded-md border border-border-subtle bg-surface-1 p-4">
          {status === 'pending' ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">Request pending</p>
                <p className="text-xs text-text-secondary mt-1">
                  Your request to join is awaiting an admin&apos;s approval.
                </p>
              </div>
              <DsButton variant="secondary" size="sm" onClick={cancel} disabled={submitting}>
                Cancel request
              </DsButton>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">Request to join</p>
                <p className="text-xs text-text-secondary mt-1">
                  This group reviews requests before granting access. Add an optional note for
                  the admins.
                </p>
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                placeholder="Optional message…"
                className="w-full px-3 py-2 rounded-sm bg-surface-2 border border-border-subtle text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent transition-colors min-h-[80px] resize-y"
              />
              <DsButton variant="primary" size="md" onClick={submit} disabled={submitting}>
                {submitting ? 'Sending…' : 'Request to join'}
              </DsButton>
            </div>
          )}

          {error && (
            <p role="alert" className="mt-3 text-xs text-status-busy">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
